import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from '~/utils/navigation';

import { LimitsModalDataType, selectLimitsModalData } from '~/components/account/ducks';
import { LIMITS_REMAINING_AMOUNT_ENDPOINT, textKeys } from '~/components/limits/constants';
import { COMPLETED, FAILED, NOTIFICATION_ENDPOINT } from '~/components/limits/spending-limit/utils/constant';
import { LimitResponseType } from '~/components/limits/types';
import { Notification } from '~/components/notifications/ducks/types';
import { ROUTE_ACCOUNT_DEPOSIT } from '~/components/router/constants';
import useTranslations from '~/components/translation/use-translations';
import { requestApi } from '~/services/api-new';
import { FlowType, trackSpendingLimitEvent } from '~/components/limits/spending-limit/utils/tracking';

type HandleNotification = (notificationProps: Partial<Notification>, option?: Partial<LimitsModalDataType>) => void;

const useEventSource = (handleNotification: HandleNotification, isDepositEnabled = true, flow = FlowType.popup) => {
	const { translations } = useTranslations(textKeys);
	const { meta } = useSelector(selectLimitsModalData) ?? {};

	const eventSourceRef = useRef<EventSource | null>(null);
	const [isActive, setIsActive] = useState(false);
	const router = useRouter();

	const handleError = (error?: string | unknown) => {
		trackSpendingLimitEvent({ flow, label: 'Bank verification failed' });

		// eslint-disable-next-line no-console
		console.error('EventSource error:', error);
		handleNotification({
			message: translations.MOBILE_GENERAL_ERROR,
			color: 'red'
		});
		eventSourceRef.current?.close();
		eventSourceRef.current = null;
		setIsActive(false);
	};

	const handleSuccess = async (limit: string) => {
		try {
			const notification = {
				message: `${translations.MOBILE_SPENDING_LIMIT_VERIFICATION_SUCCESS_NEW_LIMIT} ${limit}.`,
				color: 'green'
			} as Notification;

			if (isDepositEnabled) {
				const data = await requestApi<LimitResponseType>({ endpoint: LIMITS_REMAINING_AMOUNT_ENDPOINT });
				const maxOperation = Number(data.PARAM_MAX_OPERATION);

				if (maxOperation !== 0) {
					notification.buttonText = translations.MOBILE_HEADER_BAR_DEPOSIT;
					notification.buttonCB = () => {
						trackSpendingLimitEvent({ flow, label: 'Click deposit bank' });
						router.push(ROUTE_ACCOUNT_DEPOSIT);
					};
				}
			}

			handleNotification(notification);
			trackSpendingLimitEvent({ flow, label: 'Bank verification successful limits applied' });
		} catch (error) {
			handleError(error);
		}
	};

	const notifyPending = () => {
		trackSpendingLimitEvent({ flow, label: 'Bank verification successful' });

		handleNotification(
			{
				message: translations.MOBILE_SPENDING_LIMIT_VERIFICATION_SUCCESS_WAIT,
				color: 'neutral'
			},
			{ pending: true }
		);
	};

	const handleMessage = (event: MessageEvent) => {
		try {
			const { status, limit } = JSON.parse(event.data as string) as {
				status: string;
				limit: string;
			};

			if (status.toLowerCase() === COMPLETED) {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				handleSuccess(limit);
				eventSourceRef.current?.close();
				eventSourceRef.current = null;
				setIsActive(false);
			} else if (status.toLowerCase() === FAILED) {
				handleError(null);
			}
		} catch (error) {
			handleError(error);
		}
	};

	useEffect(() => {
		if (!isActive || eventSourceRef.current || !meta?.caseId) return;

		const url = `${NOTIFICATION_ENDPOINT}${meta?.caseId}`;
		const eventSource = new EventSource(url);
		eventSourceRef.current = eventSource;

		notifyPending();
		eventSource.onmessage = handleMessage;
		eventSource.onerror = handleError;

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isActive]);

	return [setIsActive];
};

export default useEventSource;
